import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Globe, Hand, Info, Loader2, Maximize2, Minimize2, Undo2, Unplug, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  BROWSER_VIEWPORT,
  BROWSER_VIEWPORT_MAX,
  BROWSER_VIEWPORT_MIN,
  type BrowserViewport,
} from '@hub/contracts'
import type { BrowserSession } from '@/lib/browser/use-browser-session'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/**
 * The thread's shared browser (PLAN-083) in the workspace panel: Cloudflare
 * Live View of the tab the agent drives, embedded as an iframe (scaled to
 * fit the panel, or expanded into an in-app overlay) so the user can watch —
 * and click/type in it — at any time. `BrowserActions` (take over / hand
 * back, expand, close) lives in the workspace header; `BrowserView` fills
 * the panel body.
 */

/**
 * Cloudflare's hosted Live View adds its own toolbar (URL bar, back/forward)
 * above the tab. The iframe is laid out at that natural size (tab viewport +
 * toolbar) and scaled with a CSS transform to fit its box, so nothing is
 * cropped and there are no inner scrollbars. Pointer events map through the
 * transform. In the panel the tab keeps the default 1440×900 (and its aspect
 * ratio); expanded, the tab is resized to the overlay so it renders 1:1.
 */
const LIVE_VIEW_TOOLBAR_PX = 40
/** Ignore sub-threshold size changes so a resize doesn't spam the browser. */
const VIEWPORT_RESIZE_THRESHOLD_PX = 16
const VIEWPORT_DEBOUNCE_MS = 350

/** Scale factor that fits `natural` inside `box`. */
function fitScale(
  box: { width: number; height: number },
  natural: { width: number; height: number }
): number {
  if (box.width <= 0 || box.height <= 0) return 1
  return Math.min(box.width / natural.width, box.height / natural.height, 1)
}

function clampViewport(width: number, height: number): BrowserViewport {
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(v)))
  return {
    width: clamp(width, BROWSER_VIEWPORT_MIN.width, BROWSER_VIEWPORT_MAX.width),
    height: clamp(height, BROWSER_VIEWPORT_MIN.height, BROWSER_VIEWPORT_MAX.height),
  }
}

// ── header actions ────────────────────────────────────────────────────────

export function BrowserActions({ session }: { session: BrowserSession }) {
  const { t } = useTranslation('chat')
  const { state, send, running, userControls, expanded, setExpanded } = session
  const status = state?.status ?? 'idle'
  const liveUrl = running ? state?.liveView?.url : undefined

  const pill: string | null =
    status === 'error'
      ? t('workspace.browser.error')
      : status === 'starting'
        ? t('workspace.browser.starting')
        : state?.handoff
          ? t('workspace.browser.waitingForYou')
          : userControls
            ? t('workspace.browser.userControls')
            : running
              ? t('workspace.browser.agentControls')
              : null

  return (
    <TooltipProvider delayDuration={500}>
      <div className="flex items-center gap-1">
        {running && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={t('workspace.browser.info.title')}
                className="text-muted-foreground hover:text-foreground me-0.5 flex size-7 items-center justify-center rounded-md"
              >
                <Info className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end" className="max-w-72">
              <p className="mb-1 font-medium">{t('workspace.browser.info.title')}</p>
              <ul className="list-disc space-y-1 ps-4 text-xs">
                <li>{t('workspace.browser.info.shared')}</li>
                <li>{t('workspace.browser.info.cookies')}</li>
                <li>{t('workspace.browser.info.persist')}</li>
                <li>{t('workspace.browser.info.clear')}</li>
              </ul>
            </TooltipContent>
          </Tooltip>
        )}
        {pill && (
          <span
            className={cn(
              'me-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
              userControls
                ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
                : status === 'ready'
                  ? 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200'
                  : 'bg-muted text-muted-foreground'
            )}
          >
            {pill}
          </span>
        )}
        {userControls ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="secondary"
                className="h-7 gap-1 text-xs"
                onClick={() => send({ t: 'handBack', success: true })}
              >
                <Undo2 className="size-3.5" />
                {t('workspace.browser.handBack')}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('workspace.browser.handBackHint')}</TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="secondary"
                className="h-7 gap-1 text-xs"
                disabled={!running}
                onClick={() => send({ t: 'takeControl' })}
              >
                <Hand className="size-3.5" />
                {t('workspace.browser.takeControl')}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('workspace.browser.takeControlHint')}</TooltipContent>
          </Tooltip>
        )}
        {liveUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="size-7"
                aria-label={
                  expanded ? t('workspace.browser.collapse') : t('workspace.browser.expand')
                }
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {expanded ? t('workspace.browser.collapse') : t('workspace.browser.expand')}
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label={t('workspace.browser.close')}
              disabled={!running}
              onClick={() => send({ t: 'close' })}
            >
              <X className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('workspace.browser.close')}</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}

// ── the view ──────────────────────────────────────────────────────────────

export function BrowserView({
  session,
  className,
}: {
  session: BrowserSession
  className?: string
}) {
  const { t } = useTranslation('chat')
  const { state, connection, error, running, userControls, expanded, setExpanded, send } = session
  const boxRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState({ width: 0, height: 0 })
  const viewport = state?.viewport ?? BROWSER_VIEWPORT
  const natural = { width: viewport.width, height: viewport.height + LIVE_VIEW_TOOLBAR_PX }

  // Track the box the Live View has to fit in (the panel body, or the overlay).
  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      setBox((prev) =>
        Math.abs(prev.width - r.width) < 1 && Math.abs(prev.height - r.height) < 1
          ? prev
          : { width: r.width, height: r.height }
      )
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [expanded])

  // Escape leaves the overlay.
  useEffect(() => {
    if (!expanded) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [expanded, setExpanded])

  // Expanded: ask the tab to match the overlay (minus the Live View toolbar)
  // so it renders 1:1. Collapsed: back to the default size, scaled to fit.
  useEffect(() => {
    if (!running) return
    const want = expanded
      ? clampViewport(box.width, box.height - LIVE_VIEW_TOOLBAR_PX)
      : { ...BROWSER_VIEWPORT }
    if (expanded && (box.width === 0 || box.height === 0)) return
    if (
      Math.abs(want.width - viewport.width) < VIEWPORT_RESIZE_THRESHOLD_PX &&
      Math.abs(want.height - viewport.height) < VIEWPORT_RESIZE_THRESHOLD_PX
    ) {
      return
    }
    const timer = setTimeout(() => send({ t: 'viewport', viewport: want }), VIEWPORT_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [expanded, box.width, box.height, running, viewport.width, viewport.height, send])

  const liveUrl = running ? state?.liveView?.url : undefined
  const scale = fitScale(box, natural)

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      {state?.handoff?.reason && (
        <p className="mx-3 mt-3 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          {state.handoff.reason}
        </p>
      )}
      <div
        className={cn(
          expanded
            ? 'bg-background/95 fixed inset-0 z-50 flex flex-col p-4 backdrop-blur-sm'
            : 'flex min-h-0 flex-1 flex-col'
        )}
        onClick={(e) => {
          if (expanded && e.target === e.currentTarget) setExpanded(false)
        }}
      >
        {expanded && (
          <div className="mb-3 flex items-center gap-2">
            <Globe className="size-4 shrink-0" />
            <span className="text-sm font-semibold">{t('workspace.browser.title')}</span>
            <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
              {state?.url ?? ''}
            </span>
            <Button
              size="sm"
              variant="secondary"
              className="h-8 gap-1 text-xs"
              onClick={() => setExpanded(false)}
            >
              <Minimize2 className="size-3.5" />
              {t('workspace.browser.collapse')}
            </Button>
          </div>
        )}
        <div
          ref={boxRef}
          className={cn(
            // Dark letterbox behind the scaled Live View; flush with the panel,
            // a thin top edge tinted while the user is driving.
            'relative min-h-0 w-full flex-1 overflow-hidden bg-neutral-800 border-t-2',
            userControls ? 'border-amber-400' : 'border-transparent',
            expanded && 'rounded-md'
          )}
        >
          {liveUrl ? (
            <iframe
              // Reload the hosted UI when a hand-off ends so its overlay goes away.
              key={`${liveUrl}:${state?.handoff ? 'handoff' : 'normal'}`}
              src={liveUrl}
              title={t('workspace.browser.title')}
              className="absolute top-0 left-0 border-0"
              style={{
                width: natural.width,
                height: natural.height,
                // Centred horizontally, aligned to the top.
                transform: `translate(${Math.max(0, (box.width - natural.width * scale) / 2)}px, 0) scale(${scale})`,
                transformOrigin: 'top left',
              }}
              allow="clipboard-read; clipboard-write"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-xs text-neutral-400">
              {running || connection !== 'connected' ? (
                <>
                  <Loader2 className="size-5 animate-spin" />
                  {t('workspace.browser.starting')}
                </>
              ) : (
                <>
                  <Unplug className="size-5" />
                  <span className="text-sm text-neutral-300">{t('workspace.browser.closed')}</span>
                  <span>{t('workspace.browser.closedHint')}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {userControls && (
        <p className="text-muted-foreground px-3 pb-2 text-[11px]">{t('workspace.browser.hint')}</p>
      )}
      {(error || state?.error) && (
        <p className="text-destructive px-3 pb-2 text-[11px]">{error ?? state?.error}</p>
      )}
    </div>
  )
}
